import React, { useState, useEffect } from "react";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";

//Icons
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import AvTimerOutlinedIcon from "@mui/icons-material/AvTimerOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ChatBubbleOutlineOutlinedIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";

//Menu
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";

//Dialog
import MarkSessionDialog from "../modals/MarkSessionDialog";

import { useNavigate } from "react-router-dom";

import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import isBetween from "dayjs/plugin/isBetween";
import { useAuth } from "../../../auth";
import {
  Avatar,
  Box,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { MoreVert } from "@mui/icons-material";

dayjs.extend(localizedFormat);
dayjs.extend(isBetween);

//Component Start
const AppointmentCard = ({
  ExpertName,
  ExpertId,
  appointmentDate,
  appointmentTime,
  appointmentID,
  name,
  img,
  clientType,
  joinURL,
  clientID,
  status,
  onReschedule,
  onAddNote,
  onPrescription,
  onCancel,
  selectedTab,
  BookedRegisterAs,
  can_be_rescheduled,
  can_be_cancelled,
  // Additional props to reduce API calls
  email,
  phone,
  age,
  gender,
  country,
  timezone,
  duration,
  callingMode,
  appointmentCreatedBy,
  expertFeedback
}) => {
  const [isLocked, setIsLocked] = useState(false);
  const [isMatchingConditions, setIsMatchingConditions] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [showJoin, setShowJoin] = useState(false);
  const [markSessionDialogOpen, setMarkSessionDialogOpen] = useState(false);
  const [hideSubmitButton, setHideSubmitButton] = useState(false);

  const { currentUser } = useAuth();

  useEffect(() => {
    const currentDate = dayjs();
    const appointmentDateTime = dayjs(
      `${appointmentDate} ${appointmentTime}`,
      "DD MMM YYYY hh:mm A"
    );

    //console.log("currentDate", currentDate.format("DD MMM YYYY hh:mm A"));
    const isToday = appointmentDateTime.isSame(currentDate, "day");

    const isWithinRange = currentDate.isBetween(
      appointmentDateTime.subtract(10, "minutes"),
      appointmentDateTime.add(20, "minutes"),
      null,
      "[)"
    );

    // const isWithinRange = appointmentDateTime.isBetween(
    //   currentDate.subtract(10, "minutes"),
    //   currentDate.add(20, "minutes"),
    //   null,
    //   "[)"
    // );

    // console.log(
    //   "10 mins before",
    //   appointmentDateTime.subtract(10, "minutes").format("DD MMM YYYY hh:mm A")
    // );
    // console.log(
    //   "30 mins after",
    //   appointmentDateTime.add(30, "minutes").format("DD MMM YYYY hh:mm A")
    // );
    // console.log("IsBetween", isWithinRange);

    const diffInHours = appointmentDateTime.diff(currentDate, "hours");
    const isWithin48Hours = diffInHours <= 48;

    setIsMatchingConditions(isToday && isWithinRange);
    setIsLocked(isWithin48Hours);
  }, []);

  const navigate = useNavigate();

  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMarkSessionClick = () => {
    // If expertFeedback is "incompleted", hide submit button
    const shouldHideSubmit = expertFeedback?.toLowerCase() === "incompleted";
    setHideSubmitButton(shouldHideSubmit);
    setMarkSessionDialogOpen(true);
    handleClose(); // Close the menu
  };

  const handleMarkSessionDialogClose = () => {
    setMarkSessionDialogOpen(false);
    window.location.reload();
  };

  const handleView = () => {
    // Pass the complete appointment data through navigation state to avoid API calls
    const appointmentData = {
      ID: appointmentID,
      Name: name,
      UserId: clientID,
      ProfileImage: img,
      AppDate: appointmentDate,
      AppTime: appointmentTime,
      Status: status,
      BookedRegisterAs: BookedRegisterAs,
      ExpertName: ExpertName,
      ExpertId: ExpertId,
      CallingMode: callingMode || "Video Call", // Use passed value or default
      duration: duration || "30 Minutes", // Use passed value or default
      join_url: joinURL,
      client_type: clientType,
      Country: country || "Australia", // Use passed value or default
      MobileNo: phone || "", // Use passed value
      Email: email || "", // Use passed value
      Age: age || "", // Use passed value
      Gender: gender || "", // Use passed value
      TimeZone: timezone || "IST", // Use passed value or default
      can_be_rescheduled: can_be_rescheduled,
      can_be_cancelled: can_be_cancelled,
    };
    
    navigate(`/expert/appointments/${appointmentID}`, {
      state: { 
        appointmentData, 
        patientName: name, 
        clientId: clientID,
        fromNavigation: true
      }
    });
  };

  const onCourseUndertaken = () => {
    navigate(`/expert/patients?patient=${name}&id=${clientID}&tabIndex=3`);
  };

  const onChat = (clientId, name) => {
    navigate(
      `/expert/chat?patientId=${clientId}&patientName=${name}&type=redirect`
    );
  };

  const formattedDate = appointmentDate;

  const nextCard = {
    borderRadius: "10px",
    borderColor: "#50B3EF",
    outlineStyle: "solid !important",
    outlineWidth: "5px !important",
    outlineColor: "#73589b1A !important",
  };

  const defaultCard = {
    position: "relative",
    borderRadius: "10px",
  };

  const handleJoinCall = async () => {
    const headers = {
      "X-API-Key": "76d802e426a2cb28f3760c8c8f669983f67ed775",
      "Content-Type": "application/json",
    };

    const payload = {
      user_id: currentUser?.UserId,
      appointment_id: appointmentID,
      join_status: "Joined",
      user_type: "Expert",
      current_time: dayjs().format("YYYY-MM-DD HH:mm:ss"),
    };

    //console.log("payload", payload);
    try {
      const response = await fetch(
        `https://odoo.unitedwecare.ca/new-uwc/appointment/update-join-status`,
        {
          method: "POST",
          headers,
          body: JSON.stringify(payload),
        }
      );
      const postResponse = await response.json();
      console.log("postResponse", postResponse);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  return (
    <Card
      variant="outlined"
      className={`AppointmentCard position-relative ${status == "Cancelled" && "Appointment--Cancelled"
        }`}
      sx={{
        ...(isMatchingConditions ? nextCard : defaultCard),
        overflow: "visible",
      }}
    >
      {/* Status Indicator Bar */}
      <Box
        sx={{
          position: "absolute",
          left: 0,
          top: "20px",
          width: "4px",
          height: "48px",
          bgcolor:
            status === "Cancelled"
              ? "#F97066"
              : status === "Upcoming"
                ? "#A18DBE"
                : "#47CD89",
          borderRadius: "0px 4px 4px 0px",
          zIndex: 1,
        }}
      />
      
      <CardContent sx={{ px: 2.5, pt: 2.5, pb: 2 }}>
        {/* Header Section: Avatar + Info */}
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 2.5, pl: 1 }}>
          <Avatar 
            src={img} 
            sx={{ 
              width: 56, 
              height: 56,
              flexShrink: 0,
              border: "2px solid #F0F0F0",
              boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
            }} 
          />

          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            {/* Badges Row */}
            <Box 
              sx={{ 
                display: "flex", 
                alignItems: "center", 
                gap: 0.75,
                flexWrap: "wrap",
                mb: 1.25
              }}
            >
              <Typography
                component="span"
                sx={{
                  fontWeight: 600,
                  backgroundColor: "#cbe3fb",
                  color: "#002850",
                  padding: "4px 10px",
                  borderRadius: "12px",
                  fontSize: "10px",
                  lineHeight: 1.2,
                  whiteSpace: "nowrap",
                }}
              >
                {clientType}
              </Typography>
              {BookedRegisterAs && (
                <Typography
                  component="span"
                  sx={{
                    fontWeight: 600,
                    color: "#002850",
                    backgroundColor: "#8DEAFC30",
                    textTransform: "capitalize",
                    fontSize: "10px",
                    padding: "4px 10px",
                    borderRadius: "12px",
                    lineHeight: 1.2,
                    whiteSpace: "nowrap",
                  }}
                >
                  {BookedRegisterAs}
                </Typography>
              )}
            </Box>

            {/* Name */}
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: "16px",
                lineHeight: 1.4,
                mb: 0.75,
                color: "#1A1A1A",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 1,
                WebkitBoxOrient: "vertical",
              }}
            >
              {name}
            </Typography>

            {/* Expert Name */}
            {ExpertName && (
              <Typography
                variant="body2"
                sx={{ 
                  fontSize: "12px",
                  color: "#6B7280",
                  mb: 0.5,
                  lineHeight: 1.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <Box component="span" sx={{ fontWeight: 500, color: "#374151" }}>
                  Appointment With:
                </Box>
                <Box component="span" sx={{ fontWeight: 400 }}>
                  {ExpertName}
                </Box>
              </Typography>
            )}

            {/* Created By */}
            {/* {appointmentCreatedBy && ( */}
              <Typography
                variant="body2"
                sx={{ 
                  fontSize: "12px",
                  color: "#6B7280",
                  lineHeight: 1.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <Box component="span" sx={{ fontWeight: 500, color: "#374151" }}>
                  Created By:
                </Box>
                <Box component="span" sx={{ fontWeight: 400 }}>
                  {appointmentCreatedBy ? appointmentCreatedBy?.toString() : "Patient"}
                </Box>
              </Typography>
            {/* )} */}
          </Box>
        </Box>

        {/* Status Badges */}
        <Box 
          sx={{ 
            display: "flex", 
            flexDirection: "row", 
            gap: 1,
            alignItems: "center",
            flexWrap: "wrap",
            mb: 2,
          }}
        >
          {/* Appointment Status */}
          <Tooltip title="Appointment Status" arrow={true} placement="top">
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                bgcolor: status === "Cancelled"
                  ? "#FEE4E2"
                  : status === "Completed"
                    ? "#DCFAE6"
                    : "#ECE8F2",
                px: 1.5,
                py: 0.5,
                borderRadius: "8px",
                border: `1px solid ${status === "Cancelled"
                    ? "rgba(217, 45, 32, 0.2)"
                    : status === "Completed"
                      ? "rgba(7, 148, 85, 0.2)"
                      : "rgba(101, 78, 137, 0.2)"
                  }`,
              }}
            >
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  bgcolor: status === "Cancelled"
                    ? "#D92D20"
                    : status === "Completed"
                      ? "#079455"
                      : "#654E89",
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  fontSize: "9px",
                  fontWeight: 600,
                  color: "#6B7280",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Appt
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontSize: "11px",
                  fontWeight: 600,
                  color: status === "Cancelled"
                    ? "#D92D20"
                    : status === "Completed"
                      ? "#079455"
                      : "#654E89",
                  textTransform: "capitalize",
                  letterSpacing: "0.2px",
                  ml: 0.25,
                }}
              >
                {status}
              </Typography>
            </Box>
          </Tooltip>
          
          {/* Session Status - Show based on expertFeedback */}
          <Tooltip title="Session Status" arrow={true} placement="top">
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                bgcolor: !expertFeedback 
                  ? "#F3F4F6" 
                  : expertFeedback.toLowerCase() === "completed" 
                    ? "#DCFAE6" 
                    : "#FEE4E2",
                px: 1.5,
                py: 0.5,
                borderRadius: "8px",
                border: `1px solid ${
                  !expertFeedback 
                    ? "rgba(107, 114, 128, 0.2)" 
                    : expertFeedback.toLowerCase() === "completed" 
                      ? "rgba(7, 148, 85, 0.2)" 
                      : "rgba(217, 45, 32, 0.2)"
                }`,
              }}
            >
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  bgcolor: !expertFeedback 
                    ? "#6B7280" 
                    : expertFeedback.toLowerCase() === "completed" 
                      ? "#079455" 
                      : "#D92D20",
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  fontSize: "9px",
                  fontWeight: 600,
                  color: "#6B7280",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Sess
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontSize: "11px",
                  fontWeight: 600,
                  color: !expertFeedback 
                    ? "#6B7280" 
                    : expertFeedback.toLowerCase() === "completed" 
                      ? "#079455" 
                      : "#D92D20",
                  textTransform: "capitalize",
                  letterSpacing: "0.2px",
                  ml: 0.25,
                }}
              >
                {!expertFeedback 
                  ? "Not Marked" 
                  : expertFeedback.toLowerCase() === "completed" 
                    ? "Completed" 
                    : "Incompleted"}
              </Typography>
            </Box>
          </Tooltip>
        </Box>

        <Divider sx={{ my: 0 }} />

        {/* Date/Time Section */}
        <Grid container spacing={2} sx={{ mt: 0.5, mb: 0.5 }}>
          <Grid item xs={6}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CalendarMonthOutlinedIcon 
                sx={{ 
                  fontSize: 18, 
                  color: "#6B7280",
                  flexShrink: 0,
                }} 
              />
              <Typography 
                sx={{ 
                  fontSize: "13px",
                  color: "#374151",
                  fontWeight: 500,
                  lineHeight: 1.5,
                }}
              >
                {formattedDate}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={6}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <AvTimerOutlinedIcon 
                sx={{ 
                  fontSize: 18, 
                  color: "#6B7280",
                  flexShrink: 0,
                }} 
              />
              <Typography 
                sx={{ 
                  fontSize: "13px",
                  color: "#374151",
                  fontWeight: 500,
                  lineHeight: 1.5,
                }}
              >
                {appointmentTime}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
      
      <CardActions
        className="Card--Actions"
        sx={{ 
          px: 2.5, 
          pb: 2,
          pt: 1.5,
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        {selectedTab !== "Past" ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
            {status.toLowerCase() !== "cancelled" && (
              <Button
                disableElevation
                variant="contained"
                size="medium"
                sx={{
                  flex: 1,
                  bgcolor: "var(--primary-base-color)",
                  color: "white",
                  fontSize: "0.875rem",
                  textTransform: "capitalize",
                  fontWeight: 500,
                  py: 1,
                  "&:hover": {
                    bgcolor: "var(--primary-base-color-darken)",
                    color: "white",
                  },
                  borderRadius: "10px",
                  minWidth: "auto",
                }}
                href={joinURL}
                target="_blank"
                onClick={handleJoinCall}
              >
                Join Now
              </Button>
            )}

            <Tooltip title="View Details">
              <Button
                variant="outlined"
                size="medium"
                sx={{
                  textTransform: "capitalize",
                  borderRadius: "10px",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  px: 2.5,
                  py: 1,
                  borderColor: "#E5E7EB",
                  color: "#374151",
                  "&:hover": {
                    borderColor: "#D1D5DB",
                    bgcolor: "#F9FAFB",
                  },
                  minWidth: "auto",
                }}
                onClick={handleView}
              >
                View
              </Button>
            </Tooltip>
            
            <Tooltip title="More Options">
              <IconButton
                size="medium"
                onClick={handleClick}
                sx={{
                  border: "1px solid #E5E7EB",
                  borderRadius: "10px",
                  color: "#6B7280",
                  "&:hover": {
                    bgcolor: "#F9FAFB",
                    borderColor: "#D1D5DB",
                  },
                  width: 40,
                  height: 40,
                }}
              >
                <MoreVert fontSize="small" />
              </IconButton>
            </Tooltip>
            <Menu
              id="demo-positioned-menu"
              MenuListProps={{ 
                "aria-labelledby": "demo-positioned-button",
                sx: { py: 0.5 }
              }}
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              PaperProps={{
                sx: {
                  borderRadius: "12px",
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
                  border: "1px solid #E5E7EB",
                  minWidth: 200,
                  mt: 0.5,
                }
              }}
            >
              {/* <MenuItem onClick={onAddNote}>Add Note</MenuItem> */}
              {/* <Divider /> */}
              {/* ONLY FOR AUSTRALIA */}
              {!expertFeedback && (
                <>
                  {/* <Divider /> */}
                  <MenuItem 
                    onClick={handleMarkSessionClick}
                    sx={{
                      py: 1.25,
                      px: 2,
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      color: "#374151",
                      "&:hover": {
                        bgcolor: "#F9FAFB",
                      },
                    }}
                  >
                    Mark Session
                  </MenuItem>
                </>
              )}
              {expertFeedback && expertFeedback?.toLowerCase() !== "completed" && (
                <>
                  <MenuItem 
                    onClick={handleMarkSessionClick}
                    sx={{
                      py: 1.25,
                      px: 2,
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      color: "#374151",
                    }}
                    >
                      View Session Reason
                  </MenuItem>
                </>
              )
              }
              {/* {selectedTab !== "Past" && can_be_rescheduled && (
                <> */}
                  <Divider sx={{ my: 0.5 }} />
                  <MenuItem 
                    onClick={onReschedule}
                    sx={{
                      py: 1.25,
                      px: 2,
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      color: "#374151",
                      "&:hover": {
                        bgcolor: "#F9FAFB",
                      },
                    }}
                  >
                    Reschedule
                  </MenuItem>
                {/* </>
              )} */}
              {/* {selectedTab === "Past" && (
                <>
                  <MenuItem onClick={onPrescription}>Add Prescriptions</MenuItem>
                  <Divider />
                </>
              )} */}
              {/* ONLY FOR AUSTRALIA */}
              {/* <MenuItem onClick={onCourseUndertaken}>
                Courses Undertaken
              </MenuItem> */}
              
              {
                //status.toLowerCase() !== "cancelled" && can_be_cancelled && (
                status.toLowerCase() !== "cancelled" && (
                  <>
                    <Divider sx={{ my: 0.5 }} />
                    <MenuItem 
                      onClick={onCancel}
                      sx={{
                        py: 1.25,
                        px: 2,
                        fontSize: "0.875rem",
                        fontWeight: 500,
                        color: "#DC2626",
                        "&:hover": {
                          bgcolor: "#FEF2F2",
                        },
                      }}
                    >
                      Cancel Appointment
                    </MenuItem>
                  </>
                )
              }


            </Menu>
          </Box>
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
            {status !== "Cancelled" && (
              <>
                <Tooltip title="View Details">
                  <Button
                    variant="outlined"
                    size="medium"
                    startIcon={<VisibilityIcon sx={{ fontSize: 18 }} />}
                    sx={{
                      textTransform: "capitalize",
                      borderRadius: "10px",
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      px: 2.5,
                      py: 1,
                      borderColor: "#E5E7EB",
                      color: "#374151",
                      "&:hover": {
                        borderColor: "#D1D5DB",
                        bgcolor: "#F9FAFB",
                      },
                      flex: 1,
                      minWidth: "auto",
                    }}
                    onClick={handleView}
                  >
                    View
                  </Button>
                </Tooltip>
                <Tooltip title="Start Chat">
                  <Button
                    variant="outlined"
                    size="medium"
                    startIcon={<ChatBubbleOutlineOutlinedIcon sx={{ fontSize: 18 }} />}
                    sx={{
                      textTransform: "capitalize",
                      borderRadius: "10px",
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      px: 2.5,
                      py: 1,
                      borderColor: "#E5E7EB",
                      color: "#374151",
                      "&:hover": {
                        borderColor: "#D1D5DB",
                        bgcolor: "#F9FAFB",
                      },
                      flex: 1,
                      minWidth: "auto",
                    }}
                    onClick={() => onChat(clientID, name)}
                  >
                    Chat
                  </Button>
                </Tooltip>
                <Tooltip title="More Options">
                  <IconButton
                    size="medium"
                    onClick={handleClick}
                    sx={{
                      border: "1px solid #E5E7EB",
                      borderRadius: "10px",
                      color: "#6B7280",
                      "&:hover": {
                        bgcolor: "#F9FAFB",
                        borderColor: "#D1D5DB",
                      },
                      width: 40,
                      height: 40,
                    }}
                  >
                    <MoreVert fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}

            <Menu
              id="demo-positioned-menu"
              MenuListProps={{ 
                "aria-labelledby": "demo-positioned-button",
                sx: { py: 0.5 }
              }}
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              PaperProps={{
                sx: {
                  borderRadius: "12px",
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
                  border: "1px solid #E5E7EB",
                  minWidth: 200,
                  mt: 0.5,
                }
              }}
            >
              {/* <MenuItem onClick={onAddNote}>Add Note</MenuItem> */}
              {/* <Divider /> */}
              {/* ONLY FOR AUSTRALIA */}
              {!expertFeedback && (
                <>
                  {/* <Divider /> */}
                  <MenuItem 
                    onClick={handleMarkSessionClick}
                    sx={{
                      py: 1.25,
                      px: 2,
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      color: "#374151",
                      "&:hover": {
                        bgcolor: "#F9FAFB",
                      },
                    }}
                  >
                    Mark Session
                  </MenuItem>
                </>
              )}
              {expertFeedback && expertFeedback?.toLowerCase() !== "completed" && (
                <>
                  <MenuItem 
                    onClick={handleMarkSessionClick}
                    sx={{
                      py: 1.25,
                      px: 2,
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      color: "#374151",
                    }}
                    >
                      View Session Reason
                  </MenuItem>
                </>
              )
              }
              {/* {selectedTab !== "Past" && can_be_rescheduled && (
                <> */}
                  <Divider sx={{ my: 0.5 }} />
                  <MenuItem 
                    onClick={onReschedule}
                    sx={{
                      py: 1.25,
                      px: 2,
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      color: "#374151",
                      "&:hover": {
                        bgcolor: "#F9FAFB",
                      },
                    }}
                  >
                    Reschedule
                  </MenuItem>
                {/* </>
              )} */}
              {/* {selectedTab === "Past" && (
                <>
                  <MenuItem onClick={onPrescription}>Add Prescriptions</MenuItem>
                  <Divider />
                </>
              )} */}
              {/* ONLY FOR AUSTRALIA */}
              {/* <MenuItem onClick={onCourseUndertaken}>
                Courses Undertaken
              </MenuItem> */}
              
              {
                //status.toLowerCase() !== "cancelled" && can_be_cancelled && (
                status.toLowerCase() !== "cancelled" && (
                  <>
                    <Divider sx={{ my: 0.5 }} />
                    <MenuItem 
                      onClick={onCancel}
                      sx={{
                        py: 1.25,
                        px: 2,
                        fontSize: "0.875rem",
                        fontWeight: 500,
                        color: "#DC2626",
                        "&:hover": {
                          bgcolor: "#FEF2F2",
                        },
                      }}
                    >
                      Cancel Appointment
                    </MenuItem>
                  </>
                )
              }


            </Menu>
          </Box>
        )}
      </CardActions>

      {/* Mark Session Dialog */}
      <MarkSessionDialog
        open={markSessionDialogOpen}
        onClose={handleMarkSessionDialogClose}
        appointmentID={appointmentID}
        clientID={clientID}
        hideSubmitButton={hideSubmitButton}
      />
    </Card>
  );
};

export default AppointmentCard;
